import { Button, Label, Modal, Select, TextInput } from "flowbite-react";
import { useState } from "react";
import { useGetSubcategoriesListQuery } from "@/api/ticketApi/subcategory";
import { useTranslation } from "react-i18next";

interface SubcategorySkillDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (skills: { subcategory_id: number; skill_rate: number }[]) => void;
}

export default function SubcategorySkillDialog({
  open,
  onClose,
  onSubmit,
}: SubcategorySkillDialogProps) {
  const { t } = useTranslation(["agent", "common"]);
  const { data: subcategories } = useGetSubcategoriesListQuery({
    pagination: { page: 1, pageSize: 1000 },
    filters: { is_active: true },
  });
  const [rows, setRows] = useState<
    { subcategory_id: string; skill_rate: string }[]
  >([{ subcategory_id: "", skill_rate: "" }]);

  const handleRowChange = (
    index: number,
    field: "subcategory_id" | "skill_rate",
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, { subcategory_id: "", skill_rate: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const payload = rows
      .filter((row) => row.subcategory_id && row.skill_rate)
      .map((row) => ({
        subcategory_id: parseInt(row.subcategory_id, 10),
        skill_rate: parseInt(row.skill_rate, 10),
      }));

    onSubmit(payload);
    setRows([{ subcategory_id: "", skill_rate: "" }]);
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>{t("agent:addSkills")}</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {rows.map((row, index) => (
            <div key={index} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label value={t("agent:subcategory")} />
                <Select
                  value={row.subcategory_id}
                  onChange={(e) =>
                    handleRowChange(index, "subcategory_id", e.target.value)
                  }
                >
                  <option value="">{t("agent:selectSubcategory")}</option>
                  {subcategories?.items?.map((subcategory: any) => (
                    <option
                      key={subcategory.subcategory_id}
                      value={subcategory.subcategory_id}
                    >
                      {subcategory.subcategory_name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label value={t("agent:skillRate")} />
                <TextInput
                  type="number"
                  placeholder={t("agent:enterRate")}
                  value={row.skill_rate}
                  onChange={(e) =>
                    handleRowChange(index, "skill_rate", e.target.value)
                  }
                />
              </div>
              {rows.length > 1 && (
                <div className="md:col-span-2">
                  <Button
                    color="failure"
                    size="xs"
                    onClick={() => handleRemoveRow(index)}
                  >
                    {t("common:remove")}
                  </Button>
                </div>
              )}
            </div>
          ))}
          <Button color="light" size="sm" onClick={handleAddRow}>
            {t("agent:addAnotherSkill")}
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex justify-end">
        <Button color="failure" onClick={onClose}>
          {t("common:cancel")}
        </Button>
        <Button onClick={handleSubmit}>{t("agent:saveSkills")}</Button>
      </Modal.Footer>
    </Modal>
  );
}
